// L1 thread shell — multi-turn UI wired to /api/pa/chat (PA-1b / PA-2a).

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { FireflyTourArtifact } from "@/lib/firefly/tourTypes";
import { PaActionCard } from "@/components/activity/personalAssistant/PaActionCard";
import { usePersonalAssistantThread } from "@/components/activity/personalAssistant/usePersonalAssistantThread";

export function PersonalAssistantThreadShell(props: {
  disabled?: boolean;
  currentMode: PrimaryMode | null;
  scopedOrganizationId?: string | null;
  onStartTour?: (artifact: FireflyTourArtifact) => void;
  onNavigateToMode?: (mode: PrimaryMode, highlightTarget?: string) => void;
  onExitPaMode?: () => void;
}) {
  const {
    disabled = false,
    currentMode,
    scopedOrganizationId,
    onStartTour,
    onNavigateToMode,
    onExitPaMode,
  } = props;
  const {
    listRef,
    draft,
    setDraft,
    messages,
    streaming,
    error,
    submitDraft,
    assistMode,
    getToken,
    organizationId,
    impersonationActive,
    handleProposalResolved,
    handleEditInForm,
  } = usePersonalAssistantThread({
    disabled,
    currentMode,
    scopedOrganizationId,
    onStartTour,
    onNavigateToMode,
    onExitPaMode,
  });

  const inputId = "pa-thread-composer";

  return (
    <div className="mt-2 space-y-2">
      <div
        ref={listRef}
        className="max-h-48 min-h-[4.5rem] overflow-y-auto rounded-lg border border-indigo-200/80 bg-white/90 p-2 dark:border-indigo-800/60 dark:bg-zinc-900/80"
        role="log"
        aria-live="polite"
        aria-label="Personal Assistant conversation"
      >
        {messages.length === 0 ? (
          <p className="text-[11px] text-gray-500 dark:text-zinc-400">
            Multi-turn assistant thread. Ask for help or say &quot;lead capture tour&quot; to start a
            guided walkthrough without leaving assistant mode.
          </p>
        ) : (
          <ul className="space-y-2">
            {messages.map((msg) => (
              <li key={msg.id}>
                <div
                  className={[
                    "rounded-lg px-2 py-1.5 text-xs whitespace-pre-wrap",
                    msg.role === "user"
                      ? "ml-4 bg-indigo-100 text-indigo-950 dark:bg-indigo-950/60 dark:text-indigo-100"
                      : "mr-4 bg-gray-100 text-gray-800 dark:bg-zinc-800 dark:text-zinc-200",
                  ].join(" ")}
                >
                  <span className="sr-only">{msg.role === "user" ? "You" : "Assistant"}: </span>
                  {msg.content || (streaming && msg.role === "assistant" ? "…" : "")}
                </div>
                {msg.role === "assistant" && msg.toolProposals?.length
                  ? msg.toolProposals.map((proposal) => (
                      <PaActionCard
                        key={proposal.proposalId}
                        proposal={proposal}
                        assistantMessageId={msg.id}
                        getToken={getToken}
                        organizationId={organizationId}
                        assistMode={assistMode}
                        impersonationActive={impersonationActive}
                        disabled={disabled || streaming}
                        onResolved={(proposalId, status) =>
                          handleProposalResolved(msg.id, proposalId, status)
                        }
                        onNavigate={onNavigateToMode}
                        onEditInForm={handleEditInForm}
                      />
                    ))
                  : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p className="text-[11px] text-red-700 dark:text-red-300" role="alert">
          {error}
        </p>
      ) : null}

      <label htmlFor={inputId} className="sr-only">
        Message Personal Assistant
      </label>
      <textarea
        id={inputId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void submitDraft();
          }
        }}
        disabled={disabled || streaming}
        rows={2}
        placeholder="Type a message for your assistant…"
        className="w-full resize-none rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 dark:focus:ring-indigo-700"
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void submitDraft()}
          disabled={disabled || streaming || !draft.trim()}
          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600"
        >
          {streaming ? "Thinking…" : "Send"}
        </button>
      </div>
    </div>
  );
}
